/**
 * MSAL Redirect Handler Component
 * Handles redirect promise after MSAL authentication redirect flow
 * Updates auth context with authentication result
 * 
 * CRITICAL: This is the single authority for redirect processing.
 * Only this component processes post-auth logic. Entry points (Login.tsx, useMsalAuth, MsalLogin)
 * are dumb initiators that create transactions and call loginRedirect().
 */
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ROLE_DEFINITIONS_QUERY_KEY } from '../../hooks/useRoleDefinitions';
import { useMsal } from '@azure/msal-react';
import { useAuth } from '../../context/AuthContext';
import { AUTH_API_CONFIG } from '../../config/api.config';
import { parseMsalError, isUserCancellation, isExternalUserNotInTenant } from '../../utils/msalError.utils';
import { LoadingSpinner } from '../Loading/LoadingSpinner';
import { completeLogin } from '../../utils/completeLogin.utils';
import { api, setupApiInterceptor } from '../../services/api.service';
import { clearMsalCacheSynchronously } from '../../utils/msalCache.utils';
import { buildApiScope, isExternalId } from '../../config/msal.config';
import { acquireTokenSilentDeduplicated } from '../../utils/tokenAcquisition.utils';
import { classifyTokenError, isInteractionRequired } from '../../utils/tokenErrorClassifier';
import { LoginErrorType } from '../../utils/loginError.utils';
import { authMutex } from '../../utils/authMutex.utils';
import {
  getRedirectTransaction,
  clearRedirectTransaction,
  sanitizeReturnTo,
  isValidFlowType,
  incrementRedirectAttempts,
  checkRedirectAttemptLimit,
  resetRedirectAttempts,
  isRedirectHandledRecently,
  markRedirectHandled,
  clearRedirectHandled,
  hasRedirectParameters,
  isRegistrationFlowType,
} from '../../utils/redirectTransaction.utils';
import {
  trackRedirectSuccess,
  trackTokenAcquisitionFailure,
  trackValidationFailure,
  trackRedirectProcessingTime,
  logStructuredAuthEvent,
} from '../../utils/authTelemetry.utils';
import {
  clearRegistrationRedirect,
  getRegistrationRedirect,
} from '../../utils/registrationRedirectState.utils';
import {
  finalizeRegistrationMsalRedirectHandoff,
  handleRegistrationEntraErrorRedirect,
} from '../../utils/registrationMsalRedirectHandler.utils';
import { REGISTRATION_ACCOUNT_SETUP_ROUTE } from '../../constants/routeConstants';

export const MsalRedirectHandler: React.FC = () => {
  // Always call hooks unconditionally (React requirement)
  const { instance, accounts } = useMsal();
  const { login, setIsLoggingIn, isLoggingIn, setAuthState, authState } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isMsalAuth = AUTH_API_CONFIG.providerType === 'msal';
  const [redirectHandled, setRedirectHandled] = React.useState(false);
  const [isRedirecting, setIsRedirecting] = React.useState(false);
  
  // Check if instance is ready (has getConfiguration method which stubs don't have)
  // If it's a stub, don't use it - check in useEffect instead
  const isInstanceReady = instance && typeof (instance as any).getConfiguration === 'function';

  // Check if this is a redirect callback by looking for MSAL parameters in URL
  // This check happens synchronously during render to prevent signin page from flashing
  const isRedirectCallback = React.useMemo(() => {
    if (!isMsalAuth) return false;
    const urlParams = new URLSearchParams(window.location.search);
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    // Check for common MSAL redirect parameters
    return (
      urlParams.has('code') ||
      urlParams.has('id_token') ||
      urlParams.has('access_token') ||
      hashParams.has('id_token') ||
      hashParams.has('access_token') ||
      urlParams.has('error') ||
      hashParams.has('error')
    );
  }, [isMsalAuth]);

  // Track processing state - update when redirect callback status changes
  const [isProcessingRedirect, setIsProcessingRedirect] = React.useState(isRedirectCallback);
  
  // Update processing state when redirect callback status changes
  React.useEffect(() => {
    setIsProcessingRedirect(isRedirectCallback && !redirectHandled);
  }, [isRedirectCallback, redirectHandled]);

  useEffect(() => {
    // Early return if instance is not ready (stub instance)
    if (!isMsalAuth || !isInstanceReady) {
      if (import.meta.env.DEV && isMsalAuth) {
        console.warn('[MsalRedirectHandler] Instance is not ready yet, waiting for provider...');
      }
      return;
    }
    
    if (redirectHandled) {
      return;
    }

    // Handle redirect promise after redirect authentication
    // This should only be called ONCE per page load
    // CRITICAL: All redirect processing must be serialized via auth mutex (MUST FIX #2)
    authMutex.acquire(async () => {
      // Phase 7.2 (MUST FIX #5): Track processing time breakdown
      const processingStart = Date.now();
      let msalTime = 0;
      let tokenTime = 0;
      let validationTime = 0;
      let stateUpdateTime = 0;
      // Hoisted so the catch block can preserve the account for retry
      let redirectAccount: import('@azure/msal-browser').AccountInfo | null = null;

      try {
        // Step 1: Idempotency guard - prevent StrictMode double execution
        if (isRedirectHandledRecently()) {
          setRedirectHandled(true);
          return;
        }

        // Step 2: Call handleRedirectPromise()
        const msalStart = Date.now();
        const response = await instance.handleRedirectPromise();
        msalTime = Date.now() - msalStart;
        
        // Capture account early so the catch block can preserve it for retry
        if (response?.account) redirectAccount = response.account;

        // If no response/account → return (do NOT increment attempts - Item 2 Fix)
        if (!response || !response.account) {
          setRedirectHandled(true);

          if (hasRedirectParameters()) {
            const urlParams = new URLSearchParams(window.location.search);
            const hashParams = new URLSearchParams(window.location.hash.substring(1));
            const authError = urlParams.get('error') ?? hashParams.get('error');

            if (authError) {
              clearRedirectTransaction();
              clearRedirectHandled();

              const rawDescription =
                urlParams.get('error_description') ?? hashParams.get('error_description') ?? authError;
              let errorMessage = rawDescription;
              try {
                errorMessage = decodeURIComponent(rawDescription.replace(/\+/g, ' '));
              } catch {
                errorMessage = rawDescription;
              }

              // AADSTS16000 means the external IdP account (e.g. Google) hasn't been
              // added to the tenant yet — expected for self-registration users.
              // Redirect to account-setup and surface a generic message (not the raw Azure error).
              if (
                errorMessage.includes('AADSTS16000') ||
                (errorMessage.includes('does not exist in tenant') &&
                  errorMessage.includes('identity provider') &&
                  errorMessage.includes('needs to be added as an external user'))
              ) {
                sessionStorage.setItem('msal_auth_error', parseMsalError(new Error(errorMessage)));
                handleRegistrationEntraErrorRedirect();
                setIsLoggingIn(false);
                navigate({ pathname: REGISTRATION_ACCOUNT_SETUP_ROUTE, search: '' }, { replace: true });
                return;
              }

              sessionStorage.setItem('msal_auth_error', errorMessage);

              if (handleRegistrationEntraErrorRedirect().isRegistrationFlow) {
                setIsLoggingIn(false);
                navigate({ pathname: REGISTRATION_ACCOUNT_SETUP_ROUTE, search: '' }, { replace: true });
              }
            }
          }

          return;
        }

        // Step 3: Get transaction ID
        const txn = getRedirectTransaction();
        const txnId = txn?.id ?? 'no-txn';

        // Step 4: Attempt limit check (MUST FIX #3: Per-transaction)
        if (checkRedirectAttemptLimit(txnId)) {
          console.error('[MsalRedirectHandler] Redirect attempt limit reached for transaction:', txnId);
          clearMsalCacheSynchronously(instance, response.account);
          clearRedirectTransaction();
          clearRedirectHandled();
          sessionStorage.setItem('msal_auth_error', JSON.stringify({
            type: LoginErrorType.SYSTEM_ERROR,
            message: 'Too many redirect attempts',
            userFacingMessage: "Too many sign-in attempts. Please try again later.",
            action: 'retry',
          }));
          setAuthState('portal_error');
          setIsLoggingIn(false);
          return;
        }

        // Step 5: Increment attempt counter (MUST FIX #3: Per-transaction)
        // ONLY after response with account exists
        incrementRedirectAttempts(txnId);

        const registrationRedirect = getRegistrationRedirect();
        const isRegistrationFlow =
          (txn && isRegistrationFlowType(txn.flowType)) ||
          registrationRedirect?.phase === 'redirecting' ||
          registrationRedirect?.phase === 'msal_ready';

        // Step 6: Mark handled immediately (as early as possible after valid redirect response)
        markRedirectHandled();
        setRedirectHandled(true);

        if (!isRegistrationFlow) {
          setIsLoggingIn(true);
          // Enter portal validation immediately so PortalAuthGuard shows FullPageLoader
          // ("Verifying your access…") while we acquire API token and call completeLogin —
          // avoids a gap after Azure handoff where the app had no validating UI.
          setAuthState('portal_validating');
        }

        // Step 8: Load txn (if any) and compute
        const correlationId = txn?.id ?? 'no-txn';
        const returnTo = sanitizeReturnTo(txn?.returnTo);

        // MUST FIX #7: If txn exists and !isValidFlowType(txn.flowType) → clear txn, fail to /login
        if (txn && !isValidFlowType(txn.flowType)) {
          console.error('[MsalRedirectHandler] Invalid flow type in transaction:', txn.flowType);
          clearRedirectTransaction();
          clearMsalCacheSynchronously(instance, response.account);
          sessionStorage.setItem('msal_auth_error', JSON.stringify({
            type: LoginErrorType.SYSTEM_ERROR,
            message: 'Invalid flow type',
            userFacingMessage: "An error occurred during sign-in. Please try again.",
            action: 'retry',
          }));
          setAuthState('portal_error');
          setIsLoggingIn(false);
          return;
        }

        // Step 9: Build apiScope
        const apiScope = buildApiScope();

        // Step 10: Setup interceptor with token provider (Item 2: must use deduplication)
        // CRITICAL: Token provider MUST use acquireTokenSilentDeduplicated() (Item 2)
        setupApiInterceptor(async () => {
          const result = await acquireTokenSilentDeduplicated(instance, response.account!, [apiScope]);
          return result.accessToken;
        });

        // Step 11: Acquire API token silently (mandatory if apiScope exists)
        let apiAccessToken = response.accessToken;
        if (apiScope && isExternalId()) {
          const tokenStart = Date.now();
          try {
            const tokenResult = await acquireTokenSilentDeduplicated(
              instance,
              response.account,
              [apiScope]
            );
            apiAccessToken = tokenResult.accessToken;
            tokenTime = Date.now() - tokenStart;
          } catch (tokenError) {
            tokenTime = Date.now() - tokenStart;
            
            // On token error: classifyTokenError
            const classification = classifyTokenError(tokenError);
            
            // Phase 7.2: Track token acquisition failure
            trackTokenAcquisitionFailure(classification.kind, txn?.idp);
            logStructuredAuthEvent('token_acquisition_failure', {
              correlationId,
              flowType: txn?.flowType || 'signin',
              idp: txn?.idp,
              errorKind: classification.kind,
            });
            
            // If interaction_required → clear txn, set user error, navigate /login
            if (classification.kind === 'interaction_required' || isInteractionRequired(tokenError)) {
              console.error('[MsalRedirectHandler] Token acquisition requires interaction:', classification);
              clearRedirectTransaction();
              clearMsalCacheSynchronously(instance, response.account);
              sessionStorage.setItem('msal_auth_error', JSON.stringify({
                type: LoginErrorType.AUTH_FAILURE,
                message: classification.userMessage,
                userFacingMessage: classification.userMessage,
                action: 'retry',
              }));

              if (isRegistrationFlow) {
                clearRegistrationRedirect();
                setIsLoggingIn(false);
                setAuthState('unauthenticated');
                navigate({ pathname: REGISTRATION_ACCOUNT_SETUP_ROUTE, search: '' }, { replace: true });
              } else {
                setAuthState('portal_error');
                setIsLoggingIn(false);
              }
              
              // Phase 7.2 (MUST FIX #5): Track processing time for token failure
              const totalTime = Date.now() - processingStart;
              stateUpdateTime = totalTime - msalTime - tokenTime - validationTime;
              trackRedirectProcessingTime(
                { total: totalTime, msal: msalTime, token: tokenTime, validation: validationTime, state_update: stateUpdateTime },
                'token_failure',
                txn?.idp
              );
              
              return;
            }
            
            // Else → catch-all path (log but continue with original token)
            console.warn('[MsalRedirectHandler] Token scope upgrade failed, using original token:', classification);
          }
        }

        // Step 12: Registration account setup — MSAL redirect handled here; backend completion on account setup page.
        if (isRegistrationFlow) {
          instance.setActiveAccount(response.account);
          clearRedirectTransaction();
          resetRedirectAttempts(txnId);

          finalizeRegistrationMsalRedirectHandoff(apiAccessToken, registrationRedirect);

          setIsLoggingIn(false);
          setAuthState('unauthenticated');
          setIsRedirecting(true);
          setIsProcessingRedirect(false);
          navigate(REGISTRATION_ACCOUNT_SETUP_ROUTE, { replace: true });
          return;
        }

        // Step 13: Passive redirect policy (validate-first)
        // If txn is missing: proceed anyway with completeLogin and default returnTo='/dashboard',
        // only reject if validation fails
        // (Already in portal_validating since MSAL success — do not set again; transition is no-op.)

        const validationStart = Date.now();
        const loginResult = await completeLogin(apiAccessToken, response.account);
        validationTime = Date.now() - validationStart;

        if (!loginResult.success || !loginResult.user) {
          console.error('[MsalRedirectHandler] Canonical login failed:', loginResult.error);
          
          // Phase 7.2: Track validation failure
          const errorType = loginResult.error?.type || 'SYSTEM_ERROR';
          trackValidationFailure(errorType, txn?.idp);
          logStructuredAuthEvent('validation_failure', {
            correlationId,
            flowType: txn?.flowType || 'signin',
            idp: txn?.idp,
            errorType,
            outcome: 'failure',
          });
          
          const errorToSet = loginResult.error || {
            type: LoginErrorType.SYSTEM_ERROR,
            message: 'Unknown login error',
            userFacingMessage: "We're unable to complete sign-in right now. Please try again or contact support if the problem persists.",
            action: 'retry' as const,
          };
          
          // Retryable errors (e.g. connection/timeout): keep MSAL account so "Try Again" on Login page can retry User Service
          // Non-retryable (403, 404): clear cache so user must sign in again with a different account or after access is fixed
          const isRetryable = errorToSet.action === 'retry';
          if (isRetryable) {
            instance.setActiveAccount(response.account);
          } else {
            clearMsalCacheSynchronously(instance, response.account);
          }
          
          sessionStorage.setItem('msal_auth_error', JSON.stringify(errorToSet));
          sessionStorage.setItem('login_error', 'true');
          setAuthState('portal_error');
          setIsLoggingIn(false);

          clearRedirectTransaction();
          clearRedirectHandled();
          
          // Phase 7.2 (MUST FIX #5): Track processing time for validation failure
          const totalTime = Date.now() - processingStart;
          stateUpdateTime = totalTime - msalTime - tokenTime - validationTime;
          trackRedirectProcessingTime(
            { total: totalTime, msal: msalTime, token: tokenTime, validation: validationTime, state_update: stateUpdateTime },
            'validation_failure',
            txn?.idp
          );
          
          return;
        }

        // Step 15: If validation succeeds
        const stateUpdateStart = Date.now();
        
        // Set active account ONLY after validation succeeds
        instance.setActiveAccount(response.account);

        // Update auth context – await so token/user are committed before we navigate (prevents dashboard then 401 → login error)
        const expiresIn = response.expiresOn
          ? Math.floor((response.expiresOn.getTime() - Date.now()) / 1000)
          : 3600;
        await login(apiAccessToken, null, loginResult.user, expiresIn, loginResult.userProfile);

        void queryClient.invalidateQueries({ queryKey: ROLE_DEFINITIONS_QUERY_KEY }, { cancelRefetch: false });

        // Create server-side session for telemetry (non-blocking)
        try {
          const deviceInfo = navigator.userAgent;
          await api.session.create(deviceInfo);
        } catch (apiError) {
          console.error('[MsalRedirectHandler] Backend session creation failed (non-blocking):', apiError);
        }

        // Clear login_error flag after successful login
        sessionStorage.removeItem('login_error');

        // Clear txn
        clearRedirectTransaction();

        // MUST FIX #3: Reset attempt counter on success, per-transaction
        resetRedirectAttempts(txnId);
        
        stateUpdateTime = Date.now() - stateUpdateStart;
        const totalTime = Date.now() - processingStart;

        // Phase 7.2: Track redirect success
        trackRedirectSuccess(txn?.idp, txn?.flowType || 'signin', totalTime);
        logStructuredAuthEvent('redirect_success', {
          correlationId,
          flowType: txn?.flowType || 'signin',
          idp: txn?.idp,
          outcome: 'success',
          processingTimeMs: totalTime,
        });
        
        // Phase 7.2 (MUST FIX #5): Track processing time breakdown for success
        trackRedirectProcessingTime(
          { total: totalTime, msal: msalTime, token: tokenTime, validation: validationTime, state_update: stateUpdateTime },
          'success',
          txn?.idp
        );

        // Navigate first so overlay stays until URL changes (prevents login page flash); then clear flags
        setIsRedirecting(true);
        setIsProcessingRedirect(false);
        navigate(returnTo, { replace: true });
        setIsLoggingIn(false);
      } catch (error) {
        setRedirectHandled(true);
        setIsRedirecting(false);
        setIsProcessingRedirect(false);
        
        // Get transaction for correlation ID
        const txn = getRedirectTransaction();
        const correlationId = txn?.id ?? 'no-txn';
        
        // Ignore user cancellation errors
        if (isUserCancellation(error)) {
          clearRedirectHandled();
          setIsLoggingIn(false);
          return;
        }

        // AADSTS16000: external IdP account not yet in tenant — expected for self-registration.
        // Redirect to account-setup and surface a generic message (not the raw Azure error).
        if (isExternalUserNotInTenant(error)) {
          sessionStorage.setItem('msal_auth_error', parseMsalError(error));
          handleRegistrationEntraErrorRedirect();
          clearRedirectTransaction();
          clearRedirectHandled();
          setIsLoggingIn(false);
          navigate({ pathname: REGISTRATION_ACCOUNT_SETUP_ROUTE, search: '' }, { replace: true });
          return;
        }

        console.error('[MsalRedirectHandler] MSAL redirect promise handling failed:', error);

        // Phase 7.2: Log structured error event
        const errorClassification = classifyTokenError(error);
        logStructuredAuthEvent('redirect_processing', {
          correlationId,
          flowType: txn?.flowType || 'signin',
          idp: txn?.idp,
          outcome: 'failure',
          errorKind: errorClassification.kind,
        });

        // Preserve the MSAL account so LoginErrorPage can retry UserService without re-authenticating.
        // The catch block has no access to `response` (block-scoped in try), so we use the hoisted ref.
        if (redirectAccount) {
          instance.setActiveAccount(redirectAccount);
        }

        // Parse MSAL error and store user-friendly message
        const userFriendlyMessage = parseMsalError(error);
        sessionStorage.setItem('msal_auth_error', userFriendlyMessage);

        // If Azure rejected during a registration flow, send user back to account-setup
        const registrationResult = handleRegistrationEntraErrorRedirect();

        // Clear flags
        clearRedirectTransaction();
        clearRedirectHandled();
        if (!registrationResult.isRegistrationFlow) {
          clearRegistrationRedirect();
        }
        setIsLoggingIn(false);

        if (registrationResult.isRegistrationFlow) {
          navigate({ pathname: REGISTRATION_ACCOUNT_SETUP_ROUTE, search: '' }, { replace: true });
        }
      }
    });
    // Only run once on mount - do not include 'login' to prevent re-runs
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instance, isMsalAuth]);

  // Also set active account if accounts exist but no active account is set
  useEffect(() => {
    if (!isMsalAuth || accounts.length === 0 || !isInstanceReady) {
      return;
    }

    // If no active account is set but accounts exist, set the first one
    const activeAccount = instance.getActiveAccount();
    if (!activeAccount && accounts.length > 0 && accounts[0]) {
      instance.setActiveAccount(accounts[0]);
    }
  }, [instance, accounts, isMsalAuth, isInstanceReady]);

  // While portal_validating, PortalAuthGuard renders FullPageLoader ("Verifying your access…").
  // Do not stack this overlay on top — that caused a delay before the verifying message appeared.
  // Show this overlay only for early MSAL callback handling (code/hash still processing).
  const shouldShowLoading =
    authState !== 'portal_validating' &&
    (isProcessingRedirect || isRedirecting || isLoggingIn) &&
    isRedirectCallback;
  
  if (shouldShowLoading) {
    return (
      <div
        className="fixed inset-0 z-full-page-loader flex flex-col items-center justify-center bg-auth-overlay backdrop-blur-md transition-opacity duration-300"
        role="status"
        aria-live="polite"
        aria-label="Processing authentication"
      >
        <LoadingSpinner
          layout="embedded"
          message="Processing authentication…"
          ariaLabel="Processing authentication"
        />
      </div>
    );
  }

  // This component doesn't render anything normally
  return null;
};

